import { getMyNavigation } from './roleAdmin';
import { expandLegacyNavigationKeys, normalizeUserNavigation } from '../utils/navigationEntitlements';

/**
 * FE-FIX14 (TZ №12). Хук навигации используется в Sidebar, SummaryPage,
 * ProfilePage и StatisticsPage; каждый экземпляр держал свой visibilitychange
 * и свой запрос, поэтому возврат во вкладку давал несколько GET /me/navigation.
 *
 * Владелец слушателя и in-flight запроса — этот модуль (образец:
 * changeRequestSummaryStore). Snapshot намеренно строка, чтобы
 * useSyncExternalStore сравнивал по значению: 'loading|', 'ready|k1,k2',
 * 'unavailable|', 'error|'.
 *
 * На auth-change кэш сбрасывается: ключи прежнего пользователя не должны
 * показываться следующему. Ответ запроса, начатого до смены пользователя,
 * отбрасывается (generation).
 */
const MISSING_STATUSES = new Set([404, 501]);

let state = 'loading';
let snapshot = 'loading|';
let inFlight = null;
let generation = 0;
let listenersBound = false;
const listeners = new Set();

const setValue = (nextState, nextKeys) => {
  const nextSnapshot = `${nextState}|${nextKeys.join(',')}`;
  if (nextSnapshot === snapshot) return;
  state = nextState;
  snapshot = nextSnapshot;
  listeners.forEach((listener) => listener());
};

export const refreshNavigation = () => {
  // Второй запрос, пока летит первый, — это тот же самый запрос.
  if (inFlight) return inFlight;

  if (state !== 'ready') setValue('loading', []);

  const mine = generation;
  const request = getMyNavigation()
    .then((data) => {
      if (mine !== generation) return;
      setValue('ready', expandLegacyNavigationKeys(
        normalizeUserNavigation(data).visibleKeys || [],
        data?.catalog_version ?? data?.navigation?.catalog_version,
      ));
    })
    .catch((error) => {
      if (mine !== generation) return;
      const missing = MISSING_STATUSES.has(error?.response?.status);
      // Временный сбой фонового обновления не должен снимать уже известные права.
      if (!missing && state === 'ready') return;
      setValue(missing ? 'unavailable' : 'error', []);
    })
    .finally(() => {
      if (inFlight === request) inFlight = null;
    });
  inFlight = request;
  return request;
};

const onVisible = () => {
  if (!document.hidden) refreshNavigation();
};

const onAuthChange = () => {
  generation += 1;
  inFlight = null;
  setValue('loading', []);
  if (listeners.size > 0) refreshNavigation();
};

// auth-change слушается всегда, даже без подписчиков: иначе кэш прежнего
// пользователя пережил бы логаут и мелькнул бы у следующего.
if (typeof window !== 'undefined') window.addEventListener('auth-change', onAuthChange);

const bindListeners = () => {
  if (listenersBound || typeof document === 'undefined') return;
  document.addEventListener('visibilitychange', onVisible);
  listenersBound = true;
};

const unbindListeners = () => {
  if (!listenersBound || typeof document === 'undefined') return;
  document.removeEventListener('visibilitychange', onVisible);
  listenersBound = false;
};

export const subscribeNavigation = (listener) => {
  listeners.add(listener);
  bindListeners();
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0) unbindListeners();
  };
};

export const getNavigationSnapshot = () => snapshot;

export const parseNavigationSnapshot = (value) => {
  const text = String(value);
  const separator = text.indexOf('|');
  const status = text.slice(0, separator);
  const rest = text.slice(separator + 1);
  return {
    status,
    keys: status === 'ready' ? (rest ? rest.split(',') : []) : null,
  };
};

export const resetNavigationStoreForTests = () => {
  unbindListeners();
  listeners.clear();
  inFlight = null;
  generation += 1;
  state = 'loading';
  snapshot = 'loading|';
};
